import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import configService from '../services/config.service';

// Use runtime config for server URL (fetched at startup)
const getServerUrl = () => {
  try {
    return configService.getServerUrl();
  } catch {
    // Fallback if config not loaded yet
    return window.location.origin;
  }
};

interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  googleId?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: () => void;
  logout: () => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');
      console.log('Initializing auth with token:', storedToken ? 'present' : 'missing');
      
      if (storedToken) {
        try {
          // Verify token with server
          console.log('Verifying token with server...');
          const response = await axios.get(`${getServerUrl()}/api/auth/profile`, {
            headers: {
              Authorization: `Bearer ${storedToken}`,
            },
          });
          console.log('Token verification successful:', response.data);
          setUser(response.data);
          setToken(storedToken);
        } catch (error: any) {
          console.error('Token verification failed:', error);
          localStorage.removeItem('token');
          setToken(null);
        }
      }
      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  const login = () => {
    window.location.href = `${getServerUrl()}/api/auth/google`;
  };

  const logout = async () => {
    try {
      await axios.get(`${getServerUrl()}/api/auth/logout`, {
        withCredentials: true,
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setToken(null);
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isLoading,
    isAuthenticated,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
